import { FC, useEffect, useMemo, useState } from 'react';
import { addVote, removeVote, Voting } from '@data/eve-sdk-web';
import { useConfig } from '../utils/config';
import { Modal } from '../components/Modal';
import { Stack } from '../components/Stack';
import { Headline } from '../components/Headline';
import { Copy } from '../components/Copy';
import { getColorForImageUrl, getImageUrl, getSdkImage } from '../utils/images';
import { FloatingTeaser } from '../components/FloatingTeaser';
import { ModalAd, modalsAds } from '../utils/ads';
import { interpolate } from '../utils/ui';
import { getItem, setItem } from '../utils/storage';
import { Button } from '../components/Button';
import { triggerAuth } from '../utils/auth';
import { Tiles } from '../components/Tiles';
import { getResultsStep, getVotingStep } from '../utils/votings';
import { useNotification } from '../components/Notifications';
import { useColor } from '../utils/colors';
import { Ranking } from '../components/Ranking';
import { participate } from '../utils/raffle';
import { Heart } from '../components/Icon';
import { convertClipTitleToArtists } from '../utils/hacks';
import { trackEvent } from '../utils/tracking';
import { SomFrame } from '../components/SomFrame';
import { LinkTracking } from '../components/LinkTracking';

import styles from './Battle.module.scss';

export type BattleProps = {
  votings: Voting[];
};

export const Battle: FC<BattleProps> = ({ votings }) => {
  const { context, env, name, custom, miraToken } = useConfig();
  const voting = votings[0];
  const votingStep = getVotingStep(voting);
  const results = getResultsStep(voting);
  const notification = useNotification();
  const { setColor } = useColor();
  const [showVotingModal, setShowVotingModal] = useState<boolean>(false);
  const [showThankYouModal, setShowThankYouModal] = useState<boolean>(false);
  const [showRaffleModal, setShowRaffleModal] = useState<boolean>(false);
  const [showAuthWall, setShowAuthWall] = useState<boolean>(false);
  const [showResultsModal, setShowResultsModal] = useState<boolean>(false);
  const [showVoteChangeModal, setShowVoteChangeModal] =
    useState<boolean>(false);
  const [showFloatingTeaser, setShowFloatingTeaser] = useState<boolean>(
    !getItem(`hints:floating:${custom?.battleFloatingTeaserId ?? 'default'}`),
  );
  const [showRaffleHint, setShowRaffleHint] = useState<boolean>(
    !getItem(`hints:raffle:${voting.flowId}`),
  );
  const [currentModalAd, setCurrentModalAd] = useState<ModalAd | undefined>(
    undefined,
  );
  const [renderSomFrame, setRenderSomFrame] = useState<boolean>(false);
  const [disableModalAdActions, setDisableModalAdActions] = useState<boolean>(
    modalsAds.length > 0 || Boolean(custom?.battleSomFrameFallback),
  );
  const [selectedItem, setSelectedItem] = useState<string | undefined>(
    (votingStep?.dimensions ?? []).find((dimension) => dimension.userVote)?.id,
  );
  const ranked = Array.from(results?.dimensions ?? []).sort(
    (a, b) => (b.result?.number ?? 0) - (a.result?.number ?? 0),
  );
  const dimensions = useMemo(
    () => votingStep?.dimensions ?? results?.dimensions ?? [],
    [votingStep, results],
  );
  const userVotes: string[] = dimensions
    .filter((dimension) => dimension.userVote)
    .map((vote) => vote.id);
  const currentItemVotedFor = useMemo(
    () => dimensions.find((dimension) => userVotes.includes(dimension.id)),
    [userVotes, dimensions],
  );
  const currentSelectedItem = useMemo(
    () => dimensions.find((dimension) => dimension.id === selectedItem),
    [selectedItem, dimensions],
  );
  const currentSelectedItemIndex = useMemo(
    () => dimensions.findIndex((dimension) => dimension.id === selectedItem),
    [selectedItem, dimensions],
  );
  const dimensionCount = (votingStep?.dimensions ?? []).length;

  const resetModals = () => {
    setShowThankYouModal(false);
    setShowRaffleModal(false);
    setShowVotingModal(false);
    setShowVoteChangeModal(false);
    setShowFloatingTeaser(true);
  };

  useEffect(() => {
    if (
      showThankYouModal &&
      (modalsAds.length > 0 || custom?.battleSomFrameFallback)
    ) {
      const adTimer = window.setTimeout(() => {
        if (modalsAds.length > 0) {
          setCurrentModalAd(modalsAds?.[0]);
        } else {
          setRenderSomFrame(true);
        }
      }, 1.5 * 1000);
      const actionTimer = window.setTimeout(() => {
        setDisableModalAdActions(false);
      }, 3 * 1000);
      return () => {
        window.clearTimeout(adTimer);
        window.clearTimeout(actionTimer);
      };
    }
    if (!showThankYouModal) {
      setCurrentModalAd(undefined);
      setDisableModalAdActions(
        modalsAds.length > 0 || Boolean(custom?.battleSomFrameFallback),
      );
    }
  }, [showThankYouModal]);

  return voting ? (
    <>
      <Modal
        id="voting"
        variant="medium"
        align="center"
        open={showVotingModal}
        onClose={() => {
          setShowVotingModal(false);
          setShowFloatingTeaser(true);
          trackEvent('Voting Modal Close', {
            group: voting.title.trim(),
            label: String(custom?.battleFloatingTeaserAction),
          });
        }}
        footer={
          <Stack align="center" gap="xs">
            <Button
              disabled={!selectedItem}
              label={String(custom?.battleVotingContinueAction)}
              onClick={async () => {
                if (miraToken && votingStep && selectedItem) {
                  if (currentItemVotedFor) {
                    setShowVoteChangeModal(true);
                  } else {
                    if (userVotes[0]) {
                      await removeVote(
                        context,
                        votingStep.bucketId,
                        userVotes[0],
                        1,
                      );
                    }
                    await addVote(
                      context,
                      votingStep.bucketId,
                      selectedItem,
                      1,
                    );
                    setShowThankYouModal(true);
                    trackEvent('Voting Click', {
                      group: voting.title.trim(),
                      label: `${currentSelectedItem?.title.trim()} - ${
                        currentSelectedItemIndex + 1
                      }/${dimensionCount}`,
                    });
                  }
                } else {
                  setShowAuthWall(true);
                  trackEvent('Auth Wall Show', {
                    group: voting.title.trim(),
                    label: `${currentSelectedItem?.title.trim()} - ${
                      currentSelectedItemIndex + 1
                    }/${dimensionCount}`,
                  });
                }
              }}
            />
            <Button
              variant="subtle"
              label={String(custom?.battleVotingCancelAction)}
              onClick={async () => {
                setShowVotingModal(false);
                setShowFloatingTeaser(true);
                trackEvent('Voting Modal Close', {
                  group: voting.title.trim(),
                  label: String(custom?.battleFloatingTeaserAction),
                });
              }}
            />
          </Stack>
        }>
        <Stack>
          <Headline level={5} align="center">
            {name
              ? interpolate(String(custom?.battleVotingHeadlineUser), {
                  name,
                })
              : custom?.battleVotingHeadlineAnonymous}
          </Headline>
          {custom?.battleVotingText ? (
            <Copy size="medium" align="center">
              {String(custom?.battleVotingText)}
            </Copy>
          ) : null}
          <Tiles
            items={(votingStep?.dimensions ?? []).map(
              ({ id, image, title }, index) => ({
                image: image?.url
                  ? getImageUrl(image.url, 'square')
                  : undefined,
                label: title,
                active: selectedItem === id,
                onClick: async () => {
                  if (selectedItem === id) {
                    if (voting.image) {
                      const averageColorResult = await getColorForImageUrl(
                        getImageUrl(voting.image.url, 'splash'),
                      );
                      setColor(averageColorResult);
                    }
                    setSelectedItem(undefined);
                  } else {
                    if (image?.url) {
                      const averageColorResult = await getColorForImageUrl(
                        getImageUrl(image.url, 'square'),
                      );
                      setColor(averageColorResult);
                    }
                    setSelectedItem(id);
                  }
                  trackEvent('Voting Select', {
                    group: voting.title.trim(),
                    label: `${title.trim()} - ${index + 1}/${dimensionCount}`,
                  });
                },
              }),
            )}
          />
        </Stack>
      </Modal>

      <Modal
        id="vote-change"
        variant="small"
        align="center"
        open={showVoteChangeModal}
        onClose={() => {
          setShowVoteChangeModal(false);
          trackEvent('Vote Change Modal Close', {
            group: voting.title.trim(),
            label: String(custom?.battleFloatingTeaserAction),
          });
        }}>
        <Stack>
          <Headline level={5} align="center">
            {name
              ? interpolate(String(custom?.battleVoteChangeHeadlineUser), {
                  name,
                })
              : custom?.battleVoteChangeHeadlineAnonymous}
          </Headline>
          <Copy size="medium" align="center">
            {interpolate(String(custom?.battleVoteChangeText), {
              currentItem: convertClipTitleToArtists(
                currentSelectedItem?.title ?? '',
              ),
            })}
          </Copy>
          <Stack align="center" gap="xs">
            <Button
              disabled={!selectedItem}
              label={String(custom?.battleVotingContinueAction)}
              onClick={async () => {
                if (miraToken && votingStep && selectedItem) {
                  if (userVotes[0]) {
                    await removeVote(
                      context,
                      votingStep.bucketId,
                      userVotes[0],
                      1,
                    );
                  }
                  await addVote(context, votingStep.bucketId, selectedItem, 1);
                  setShowThankYouModal(true);
                  trackEvent('Voting Click', {
                    group: voting.title.trim(),
                    label: `${currentSelectedItem?.title.trim()} - ${
                      currentSelectedItemIndex + 1
                    }/${dimensionCount}`,
                  });
                } else {
                  setShowAuthWall(true);
                  trackEvent('Auth Wall Show', {
                    group: voting.title.trim(),
                    label: `${currentSelectedItem?.title.trim()} - ${
                      currentSelectedItemIndex + 1
                    }/${dimensionCount}`,
                  });
                }
                trackEvent('Vote Change Modal Click', {
                  group: voting.title.trim(),
                  label: String(custom?.battleFloatingTeaserAction),
                });
              }}
            />
            <Button
              variant="subtle"
              label={String(custom?.battleVotingCancelAction)}
              onClick={async () => {
                setShowVoteChangeModal(false);
                trackEvent('Vote Change Modal Close', {
                  group: voting.title.trim(),
                  label: String(custom?.battleFloatingTeaserAction),
                });
              }}
            />
          </Stack>
        </Stack>
      </Modal>

      <Modal
        id="thank-you"
        variant="medium"
        open={showThankYouModal}
        onClose={() => {
          setShowThankYouModal(false);
          trackEvent('Thank You Modal Close', {
            group: voting.title.trim(),
            label: String(custom?.battleFloatingTeaserAction),
          });
        }}
        coverImage={
          currentItemVotedFor?.image?.url
            ? getImageUrl(currentItemVotedFor.image.url, 'teaser')
            : getSdkImage(env, 'the-voice-kids.jpg')
        }
        ad={currentModalAd ?? (renderSomFrame ? <SomFrame /> : undefined)}
        footer={
          <Stack gap="xs" align="center">
            <Button
              disabled={disableModalAdActions}
              label={String(custom?.battleThankYouProceedAction)}
              onClick={() => {
                setShowThankYouModal(false);
                if (showRaffleHint) {
                  setShowRaffleModal(true);
                } else {
                  resetModals();
                }
                trackEvent('Thank You Modal Click', {
                  group: voting.title.trim(),
                  label: String(custom?.battleFloatingTeaserAction),
                });
              }}
            />
          </Stack>
        }>
        <Stack gap="xs">
          <div className={styles['battle__intro-icon']}>
            <Heart size={48} />
          </div>
          <Headline level={4} align="center">
            {name
              ? interpolate(String(custom?.battleThankYouStepHeadlinUser), {
                  name,
                })
              : String(custom?.battleThankYouStepHeadlineAnonymous)}
          </Headline>
          <Copy align="center">{custom?.battleThankYouStepText}</Copy>
        </Stack>
      </Modal>

      {custom?.battleRaffleId ? (
        <Modal
          id="raffle"
          variant="medium"
          open={showRaffleModal}
          onClose={() => {
            setShowRaffleModal(false);
            trackEvent('Raffle Modal Close', {
              group: voting.title.trim(),
              label: String(custom.battleFloatingTeaserAction),
            });
          }}
          coverImage={getSdkImage(env, String(custom?.battleRaffleImage))}
          footer={
            <Stack gap="xs" align="center">
              <Button
                label={String(custom?.battleRaffleProceedAction)}
                onClick={async () => {
                  setItem(`hints:raffle:${voting.flowId}`, true);
                  setShowRaffleHint(false);
                  if (miraToken && custom?.battleRaffleId) {
                    await participate(
                      String(custom.battleRaffleId),
                      miraToken,
                      custom.battleRaffleProvider
                        ? String(custom.battleRaffleProvider)
                        : undefined,
                    );
                    notification({
                      id: String(Math.random()),
                      title: name
                        ? `Danke für deine Teilnahme, ${name}!`
                        : 'Danke für deine Teilnahme!',
                      text: 'Wir drücken die Daumen!',
                    });
                  }
                  setShowRaffleModal(false);
                  resetModals();
                  trackEvent('Raffle Modal Click', {
                    group: voting.title.trim(),
                    label: String(custom.battleFloatingTeaserAction),
                  });
                }}
              />
              <Button
                variant="ghost"
                label={String(custom?.battleRaffleRevertAction)}
                onClick={async () => {
                  setItem(`hints:raffle:${voting.flowId}`, true);
                  setShowRaffleHint(false);
                  setShowRaffleModal(false);
                  trackEvent('Raffle Modal Close', {
                    group: voting.title.trim(),
                    label: String(custom.battleFloatingTeaserAction),
                  });
                  resetModals();
                }}
              />
            </Stack>
          }>
          <Stack gap="xs">
            {custom?.battleRaffleProvider ? (
              <div className={styles['battle__intro-logo']}>
                <svg width="148" height="32" viewBox="0 0 148 32">
                  <g fillRule="nonzero" fill="none">
                    <path
                      d="m84.466 15.976-4.147-5.62h-1.718v8.343h1.62v-5.767l4.22 5.767h1.645v-8.343h-1.62v5.62Zm-10.576-5.62h1.62v8.343h-1.62v-8.343Zm-28.933 4.466h2.945v1.57c-.295.27-.638.491-1.006.639-.418.172-.86.27-1.325.27a3.145 3.145 0 0 1-2.43-1.056c-.638-.687-.957-1.57-.957-2.625 0-1.056.32-1.939.957-2.626a3.158 3.158 0 0 1 2.43-1.03c.515 0 1.03.122 1.472.367.442.246.81.565 1.08.982l1.423-.81c-.957-1.399-2.282-2.11-3.975-2.11-1.473 0-2.675.49-3.681 1.447-.982.957-1.497 2.234-1.497 3.78 0 1.546.49 2.822 1.497 3.779 1.006.957 2.233 1.448 3.68 1.448 1.645 0 2.994-.614 4.074-1.84v-3.78h-4.662l-.025 1.595Zm7.534 3.877h5.914v-1.448h-4.32v-2.085h4.222v-1.448h-4.221v-1.914h4.319v-1.448H52.49v8.343Zm15.853-2.184-1.694-6.16h-1.276l-1.717 6.136-1.571-6.135h-1.791l2.429 8.343h1.693l1.595-5.89 1.595 5.89h1.694l2.454-8.343h-1.792l-1.62 6.16Zm26.699-.54-4.123-5.62h-1.718V18.7h1.62v-5.767l4.221 5.767h1.644v-8.343h-1.619v5.62h-.025Zm48.785-5.62h-1.963l-3.362 8.344h1.816l.614-1.57h3.828l.613 1.57h1.792l-3.338-8.343Zm-2.405 5.35 1.424-3.631 1.423 3.632h-2.847Zm-6.625.27-4.148-5.62h-1.717V18.7h1.619v-5.767l4.221 5.767h1.644v-8.343h-1.619v5.62Zm-17.915-1.226c.393-.442.589-1.031.589-1.718a2.549 2.549 0 0 0-.736-1.89c-.491-.515-1.178-.76-2.037-.76h-3.852v8.343h1.619v-3.018h1.399l1.841 3.018h1.865l-2.111-3.166a2.535 2.535 0 0 0 1.423-.81Zm-4.441-.516v-2.43h2.061c.393 0 .736.123.957.344.246.221.368.54.344.884.024.343-.123.662-.344.883-.221.221-.564.344-.957.344l-2.061-.025Zm7.779 4.466h5.914v-1.448h-4.319v-2.085h4.221v-1.448h-4.221v-1.914h4.319v-1.448h-5.914v8.343ZM102.65 8.59l-3.852 10.11h1.963l2.994-8.319 1.398 3.878h-1.374l-.515 1.448h2.429l1.031 2.993h1.963L104.81 8.59h-2.16ZM31.411 6.307a2.006 2.006 0 0 0-.22-.467c-.393-.76-1.007-1.447-1.792-2.11l-.025-.025C26.601 1.472 21.644 0 16 0S5.374 1.497 2.601 3.73l-.024.025c-.786.638-1.4 1.35-1.792 2.11-.073.147-.147.295-.22.466-.05.098-.074.221-.123.32A8.708 8.708 0 0 0 0 9.398c.074 5.448 4.564 12.123 7.141 15.705 2.258 3.141 4.785 6.283 7.804 6.798.343.073.687.098 1.03.098.344 0 .688-.024 1.031-.098 3.018-.491 5.546-3.632 7.804-6.798 2.208-3.092 5.89-8.54 6.871-13.472H15.509c0 .908.295 1.767.835 2.503.907 1.227 1.35 1.546 2.895 1.546h3.215c.417 0 .785.196 1.03.54l1.031 1.472c.025.025.05.05.05.074a1.226 1.226 0 0 1-1.08 1.84H8.589a1.215 1.215 0 0 1-1.08-1.791l7.436-13.718c.22-.368.613-.564 1.03-.564.418 0 .81.22 1.031.564l.05.098c.024.025.024.05.048.074l2.332 4.466c.196.393.588.638 1.006.663h11.484a8.71 8.71 0 0 0-.441-2.749.868.868 0 0 0-.074-.343Zm13.62 21.276c0 1.767-.957 2.601-2.037 2.601a1.888 1.888 0 0 1-1.3-.589v1.84l-1.007.516v-7.043h1.006v.49c.368-.343.81-.588 1.35-.588 1.104.024 1.988.834 1.988 2.773Zm-1.006.049c0-1.3-.418-1.865-1.154-1.865-.49 0-.908.319-1.178.662v2.16c.221.294.687.687 1.203.687.736-.024 1.129-.564 1.129-1.644Zm6.6-.147c0 1.693-1.005 2.699-2.33 2.699-1.301 0-2.258-1.08-2.258-2.675 0-1.693 1.006-2.675 2.307-2.675 1.3 0 2.282 1.031 2.282 2.65Zm-1.03.024c0-1.055-.49-1.718-1.3-1.718-.761 0-1.227.688-1.227 1.694 0 1.128.515 1.742 1.276 1.742.76 0 1.251-.785 1.251-1.718Zm8.221-2.576-1.423 5.128h-.86l-.686-2.6c-.099-.393-.221-.86-.27-1.105-.05.245-.148.711-.27 1.104l-.687 2.577h-.884l-1.423-5.129h1.006l.613 2.503c.099.393.221.883.246 1.129.049-.245.172-.736.294-1.129l.688-2.503h.81l.687 2.503c.098.393.22.859.27 1.129.048-.245.147-.736.245-1.129l.613-2.503h1.031v.024Zm5.129 2.478c0 .196-.025.344-.025.466h-3.386c.098.982.662 1.4 1.3 1.4.442 0 .786-.148 1.13-.418l.588.638c-.442.442-.981.687-1.767.687-1.202 0-2.233-.957-2.233-2.675 0-1.742.908-2.675 2.258-2.675 1.423 0 2.135 1.178 2.135 2.577Zm-1.03-.368c-.05-.712-.393-1.3-1.203-1.3-.687 0-1.08.466-1.178 1.3h2.38Zm5.324-2.012-.27.957a1.77 1.77 0 0 0-.785-.197c-.638 0-1.006.442-1.006 1.326v2.944h-1.006v-5.128h1.006v.49c.27-.368.712-.613 1.178-.589.442 0 .687.074.883.197Zm4.982 2.38c0 .196-.025.344-.025.466H68.81c.098.982.662 1.4 1.3 1.4.442 0 .786-.148 1.13-.418l.588.638c-.442.442-.981.687-1.767.687-1.202 0-2.233-.957-2.233-2.675 0-1.742.908-2.675 2.258-2.675 1.423 0 2.135 1.178 2.135 2.577Zm-1.03-.368c-.05-.712-.393-1.3-1.203-1.3-.687 0-1.08.466-1.178 1.3h2.38Zm6.355 3.043H76.54v-.49c-.368.343-.81.588-1.35.588-1.104 0-1.987-.81-1.987-2.773 0-1.767.956-2.601 2.036-2.601.491.024.957.22 1.3.589v-1.963l1.007-.516v7.166Zm-.982-1.497v-2.16c-.22-.294-.687-.687-1.202-.687-.736 0-1.129.54-1.129 1.645 0 1.3.417 1.864 1.153 1.864.491 0 .909-.319 1.178-.662Zm9.448-1.006c0 1.767-.957 2.601-2.037 2.601a1.888 1.888 0 0 1-1.3-.589v.49h-.982V23.46l1.006-.515v2.478c.368-.343.81-.589 1.35-.589 1.08 0 1.963.81 1.963 2.749Zm-1.006.049c0-1.3-.417-1.865-1.153-1.865-.491 0-.908.319-1.178.662v2.16c.22.294.687.687 1.202.687.737-.024 1.13-.564 1.13-1.644Zm6.454-2.7-2.576 6.945h-1.08l.736-1.914-1.89-5.055h1.105l.834 2.528c.147.441.393 1.202.442 1.497.098-.295.294-1.056.466-1.473l.859-2.527h1.104Z"
                      fill="#FFF"
                    />
                    <path
                      d="m142.205 27.215-1.35-1.178 1.35-1.178c.147-.123.147-.32.024-.466-.122-.148-.319-.148-.466-.025l-1.423 1.252-1.424-1.252a.34.34 0 0 0-.466.025c-.123.122-.098.343.025.466l1.349 1.178-1.349 1.178c-.148.122-.148.319-.025.466.123.147.319.147.466.024l1.424-1.251 1.423 1.252a.39.39 0 0 0 .319.073c.123-.049.196-.123.221-.245.024-.123-.025-.221-.098-.32Zm4.613 0-1.349-1.178 1.349-1.178c.098-.074.123-.196.098-.32-.024-.122-.098-.22-.22-.245-.123-.049-.246-.024-.319.074l-1.424 1.252-1.423-1.252a.39.39 0 0 0-.319-.073c-.123.049-.196.122-.221.245a.366.366 0 0 0 .098.319l1.35 1.178-1.35 1.178c-.098.073-.122.196-.098.319.025.122.098.22.221.245.123.05.245.025.319-.073l1.423-1.252 1.424 1.252a.34.34 0 0 0 .466-.025.32.32 0 0 0-.025-.466Zm-9.668-2.945a.316.316 0 0 0-.246.098.317.317 0 0 0-.098.245v2.872a.35.35 0 0 0 .344.343.351.351 0 0 0 .343-.343v-2.872a.335.335 0 0 0-.343-.343Zm-2.086.712a.488.488 0 0 0-.393.171.58.58 0 0 0-.172.393v1.055a1.17 1.17 0 0 1-1.178 1.178h-1.766a.351.351 0 0 1-.344-.343c0-.197.147-.344.344-.344h1.693c.147 0 .294-.05.392-.172a.575.575 0 0 0 .172-.392v-1.056c0-.319.123-.613.344-.834.221-.22.515-.344.834-.344h.834c.172 0 .344.148.344.344a.335.335 0 0 1-.344.344h-.76Z"
                      fill="#5DC200"
                    />
                    <path fill="#FF3700" d="M126.548 23.018h-8.81v5.89h8.81z" />
                    <path
                      d="M121.001 28.05c2.11 0 3.73-1.694 3.73-3.78 0-.49-.147-1.153-.344-1.473-.564.123-1.153.197-1.717.246-.197.81-.859 1.178-1.473 1.276-.024.172-.098.785-.196 1.178a3.612 3.612 0 0 0 1.129-.27c-.098.319-.393.81-.761 1.08a2.287 2.287 0 0 1-2.454.27 2.327 2.327 0 0 1-1.325-2.086v1.35a3.74 3.74 0 0 0 3.411 2.208Z"
                      fill="#FFF"
                    />
                    <path
                      d="M95.754 22.797h6.307v1.767h-6.307v-1.767Zm6.307 2.111v4.319h-6.307s.785-2.086 2.797-3.337c1.939-1.178 3.51-.982 3.51-.982Z"
                      fill="#E40040"
                    />
                    <path
                      d="M107.898 27.558c-1.08-2.184-.442-3.68.196-4.196a3.278 3.278 0 0 1 1.865-.564c.172 0 .344.024.515.049-1.276.024-2.601 2.478-2.576 4.711Z"
                      fill="#932669"
                    />
                    <path
                      d="M107.848 27.558c.589-1.938 2.724-4.662 3.632-4.417a3.34 3.34 0 0 1 1.349 1.325c-.392.54-3.509 2.454-4.981 3.092Z"
                      fill="#B42457"
                    />
                    <path
                      d="M107.949 27.583c1.399.098 5.105-1.055 5.203-2.135a3.148 3.148 0 0 1-.27 2.11c-.54.957-2.798 1.325-4.933.025Z"
                      fill="#EB8B2D"
                    />
                    <path
                      d="M107.949 27.583c1.693 1.595 3.338 1.546 4.074 1.03a3.411 3.411 0 0 1-1.546.688c-1.08.171-1.816-.221-2.528-1.718Z"
                      fill="#FFDA1A"
                    />
                    <path
                      d="M107.876 27.558c.147.81.515 1.375 1.03 1.595a3.538 3.538 0 0 1-1.227-.687c-.172-.245.147-.785.197-.908Z"
                      fill="#91C33F"
                    />
                    <path
                      d="M107.922 27.607c-.074.05-.417.32-.614.442a2.723 2.723 0 0 1-.515-1.03c.172.417.908.54 1.129.588Z"
                      fill="#1BAD89"
                    />
                    <path
                      d="M107.923 27.607c-.294-.147-1.128-.613-1.251-1.3v-.172c0-.638.196-1.252.515-1.742-.638 1.325.295 2.65.736 3.214Z"
                      fill="#0061A1"
                    />
                  </g>
                </svg>
              </div>
            ) : (
              <div className={styles['battle__intro-icon']}>
                <Heart size={48} />
              </div>
            )}
            <Headline level={4} align="center">
              {custom?.battleRaffleTitle}
            </Headline>
            <Copy align="center">{custom?.battleRaffleText}</Copy>
            <Copy align="center" size="tiny">
              <LinkTracking>
                <span
                  dangerouslySetInnerHTML={{
                    __html: String(custom?.battleRaffleLegal),
                  }}
                />
              </LinkTracking>
            </Copy>
          </Stack>
        </Modal>
      ) : null}

      <Modal
        id="auth"
        variant="small"
        align="center"
        open={showAuthWall}
        onClose={() => {
          setShowAuthWall(false);
          trackEvent('Auth Wall Modal Close', {
            group: voting.title.trim(),
            label: String(custom?.battleFloatingTeaserAction),
          });
        }}>
        <Stack>
          <svg
            viewBox="0 0 52 64"
            className={styles['battle__auth-wall-illustration']}>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M26.5 6.74994C19.0442 6.74994 13 12.7941 13 20.2499C13 27.7058 19.0442 33.7499 26.5 33.7499C33.9558 33.7499 40 27.7058 40 20.2499C40 12.7941 33.9558 6.74994 26.5 6.74994ZM26.5 9.74994C32.299 9.74994 37 14.451 37 20.2499C37 26.0489 32.299 30.7499 26.5 30.7499C20.701 30.7499 16 26.0489 16 20.2499C16 14.451 20.701 9.74994 26.5 9.74994Z"
              fill="currentColor"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M33.1466 38.2499C42.5444 38.2499 50.3855 45.7558 51.4906 55.5823C51.5832 56.4055 50.9909 57.148 50.1677 57.2405C49.3444 57.3331 48.602 56.7408 48.5094 55.9176C47.5833 47.6826 41.1536 41.4258 33.4719 41.2536L33.1466 41.2499H18.8508C11.0334 41.2499 4.43862 47.555 3.49042 55.8962C3.39684 56.7193 2.65371 57.3107 1.83059 57.2171C1.00746 57.1236 0.416042 56.3804 0.509614 55.5573C1.61226 45.8576 9.27831 38.4265 18.5201 38.253L18.8508 38.2499H33.1466Z"
              fill="currentColor"
            />
          </svg>
          <Headline level={5} align="center">
            {custom?.battleAuthWallHeadline}
          </Headline>
          <Copy size="medium" align="center">
            {custom?.battleAuthWallText}
          </Copy>
          <Stack align="center" gap="xs">
            <Button
              label={String(custom?.battleAuthWallContinueAction)}
              onClick={async () => {
                triggerAuth();
                trackEvent('Auth Wall Modal Click', {
                  group: voting.title.trim(),
                  label: String(custom?.battleFloatingTeaserAction),
                });
              }}
            />
            <Button
              variant="subtle"
              label={String(custom?.battleAuthWallCancelAction)}
              onClick={async () => {
                setShowAuthWall(false);
                trackEvent('Auth Wall Modal Close', {
                  group: voting.title.trim(),
                  label: String(custom?.battleFloatingTeaserAction),
                });
              }}
            />
          </Stack>
        </Stack>
      </Modal>

      <Modal
        id="results"
        variant="medium"
        open={showResultsModal}
        onClose={() => {
          setShowResultsModal(false);
        }}>
        <Stack gap="xl">
          <Stack gap="xs">
            <Headline level={4} align="center">
              {String(custom?.battleResultsModalHeadline)}
            </Headline>
            <Headline level={6} align="center">
              {name
                ? interpolate(String(custom?.battleResultsModalSublineUser), {
                    name,
                  })
                : String(custom?.battleResultsModalSublineAnonymous)}
            </Headline>
            {custom?.battleResultsModalIntro ? (
              <Copy align="center">
                {String(custom?.battleResultsModalIntro)}
              </Copy>
            ) : null}
          </Stack>
          <Ranking
            items={ranked.map(({ id, image, title }) => ({
              image: image?.url ? getImageUrl(image.url, 'square') : undefined,
              label: title,
              active: selectedItem === id,
            }))}
          />
        </Stack>
      </Modal>

      {custom && custom.battleFloatingTeaser ? (
        <FloatingTeaser
          visible={showFloatingTeaser}
          title={
            name
              ? interpolate(String(custom.battleFloatingTeaserTitleUser), {
                  name,
                })
              : String(custom.battleFloatingTeaserTitleAnonymous)
          }
          action={String(custom.battleFloatingTeaserAction)}
          image={String(custom.battleFloatingTeaserImage)}
          onClick={() => {
            if (custom.battleFloatingTeaserUrl) {
              window.open(String(custom.battleFloatingTeaserUrl));
            } else {
              setShowVotingModal(true);
              setShowFloatingTeaser(false);
            }
            trackEvent('Voting Floating Teaser Click', {
              group: voting.title.trim(),
              label: String(custom.battleFloatingTeaserAction),
            });
          }}
        />
      ) : null}
    </>
  ) : null;
};
